import { computed, ref, watch, type Ref } from 'vue'
import { noticeMessagePopup } from '~/utils/common'
import { uploadToS3 } from '~/utils/upload'
import {
    ALMIGHTY_AUDIO_MAX_COUNT,
    ALMIGHTY_IMAGE_MAX_COUNT,
    DANCE_25_AUDIO_MAX_COUNT,
    DANCE_25_IMAGE_MAX_COUNT,
    DANCE_25_MEDIA_DURATION_LIMITS,
    DANCE_25_VIDEO_MAX_COUNT,
    ALMIGHTY_VIDEO_MAX_COUNT,
    ALMIGHTY_IMAGE_RESOLUTION_LIMIT,
    ALMIGHTY_IMAGE_SIZE_LIMIT,
    formatAlmightyDurationSeconds,
    getDance25FileSizeLabel,
    getAlmightyFileKind,
    getAlmightyMediaReadErrorMessage,
    getAlmightyValidationMessage,
    getImageResolution,
    isResolutionWithinLimit,
    isDance25VideoResolutionValid,
    isSupportedAlmightyImageFile,
    readAlmightyAudioDuration,
    readAlmightyVideoMetadata,
    validateAlmightyAudioLimits,
    validateAlmightyImageCount,
    validateAlmightyVideoLimits,
    validateDance25ReferenceFile,
    type AlmightyReferenceItem,
    type AlmightyReferenceKind,
    type AlmightyValidationIssue,
} from '~/utils/almightyReference'
import { isDance25Model } from '~/utils/videoModelCapabilities'

type UseSeedanceAllroundReferencesOptions = {
    items: Ref<AlmightyReferenceItem[]>
    isLoggedIn: Ref<boolean>
    locale: Ref<string>
    tipTitle: string
    modelKey?: Ref<string | undefined>
    onRequireLogin?: () => void
}

export const useSeedanceAllroundReferences = (
    options: UseSeedanceAllroundReferencesOptions
) => {
    const { items, isLoggedIn, locale, tipTitle, modelKey, onRequireLogin } =
        options
    const isDance25 = computed(() => isDance25Model(modelKey?.value))
    const imageMaxCount = computed(() =>
        isDance25.value ? DANCE_25_IMAGE_MAX_COUNT : ALMIGHTY_IMAGE_MAX_COUNT
    )
    const videoMaxCount = computed(() =>
        isDance25.value ? DANCE_25_VIDEO_MAX_COUNT : ALMIGHTY_VIDEO_MAX_COUNT
    )
    const audioMaxCount = computed(() =>
        isDance25.value ? DANCE_25_AUDIO_MAX_COUNT : ALMIGHTY_AUDIO_MAX_COUNT
    )
    const durationLimits = computed(() =>
        isDance25.value ? DANCE_25_MEDIA_DURATION_LIMITS : {}
    )
    const validationContext = computed(() => ({
        imageMaxCount: imageMaxCount.value,
        videoMaxCount: videoMaxCount.value,
        audioMaxCount: audioMaxCount.value,
        ...durationLimits.value,
    }))
    const fileInputRef = ref<HTMLInputElement | null>(null)
    const isHandlingReferences = ref(false)

    const isZh = computed(() => locale.value.toLowerCase().startsWith('zh'))

    const images = computed(() =>
        items.value.filter(item => item.kind === 'image')
    )
    const videos = computed(() =>
        items.value.filter(item => item.kind === 'video')
    )
    const audios = computed(() =>
        items.value.filter(item => item.kind === 'audio')
    )
    const videoDurationTotal = computed(() =>
        videos.value.reduce(
            (sum, item) => sum + Math.max(0, Number(item.duration || 0)),
            0
        )
    )
    const audioDurationTotal = computed(() =>
        audios.value.reduce(
            (sum, item) => sum + Math.max(0, Number(item.duration || 0)),
            0
        )
    )
    const hasVisualReference = computed(() => {
        return images.value.length > 0 || videos.value.length > 0
    })

    watch(
        [imageMaxCount, videoMaxCount, audioMaxCount],
        () => {
            const counts = { image: 0, video: 0, audio: 0 }
            const limits = {
                image: imageMaxCount.value,
                video: videoMaxCount.value,
                audio: audioMaxCount.value,
            }
            const nextItems = items.value.filter(item => {
                counts[item.kind] += 1
                return counts[item.kind] <= limits[item.kind]
            })
            if (nextItems.length !== items.value.length) {
                items.value = nextItems
            }
        },
        { immediate: true }
    )
    const hasUploadingItems = computed(() =>
        items.value.some(item => item.loading)
    )
    const hasBusyItems = computed(() => {
        return isHandlingReferences.value || hasUploadingItems.value
    })

    const showNotice = (
        message: string,
        type: 'success' | 'warning' | 'error' = 'warning'
    ) => {
        noticeMessagePopup(message, tipTitle, type, 3000, '')
    }

    const createReferenceId = (): string =>
        `almighty-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

    const showValidationIssue = (issue: AlmightyValidationIssue | null) => {
        if (!issue) return true
        showNotice(
            getAlmightyValidationMessage(
                issue,
                locale.value,
                validationContext.value
            )
        )
        return false
    }

    const validateImageFile = async (file: File) => {
        if (!isDance25.value && !isSupportedAlmightyImageFile(file)) {
            showNotice(
                isZh.value
                    ? '仅支持上传图片、视频和音频文件'
                    : 'Only image, video and audio files are supported'
            )
            return false
        }
        if (!isDance25.value && file.size > ALMIGHTY_IMAGE_SIZE_LIMIT) {
            showNotice(
                isZh.value
                    ? '单张图片大小不能超过 100MB'
                    : 'Each image must be within 100MB'
            )
            return false
        }

        try {
            const resolution = await getImageResolution(file)
            if (
                !isResolutionWithinLimit(
                    resolution,
                    ALMIGHTY_IMAGE_RESOLUTION_LIMIT
                )
            ) {
                showNotice(
                    isZh.value
                        ? '图片分辨率不能超过 4096 x 4096'
                        : 'Image resolution must be within 4096 x 4096'
                )
                return false
            }
            return true
        } catch (error) {
            showNotice(
                getAlmightyMediaReadErrorMessage(error, locale.value),
                'error'
            )
            return false
        }
    }

    const insertLoadingItem = (
        kind: AlmightyReferenceKind,
        name: string
    ): string => {
        const id = createReferenceId()
        items.value.push({
            id,
            kind,
            name,
            loading: true,
        })
        return id
    }

    const replaceLoadingItem = (
        id: string,
        nextItem: AlmightyReferenceItem | null
    ) => {
        const index = items.value.findIndex(item => item.id === id)
        if (index === -1) return
        if (!nextItem) {
            items.value.splice(index, 1)
            return
        }
        items.value.splice(index, 1, nextItem)
    }

    const uploadReference = async ({
        file,
        kind,
        duration,
    }: {
        file: File
        kind: AlmightyReferenceKind
        duration?: number
    }) => {
        const loadingId = insertLoadingItem(kind, file.name)
        try {
            const url = await uploadToS3(file)
            if (!url) {
                throw new Error(
                    isZh.value
                        ? '上传失败，请稍后重试'
                        : 'Upload failed, please try again'
                )
            }
            replaceLoadingItem(loadingId, {
                id: createReferenceId(),
                kind,
                url,
                name: file.name,
                duration,
            })
            return true
        } catch (error) {
            replaceLoadingItem(loadingId, null)
            showNotice(
                (error as any)?.message ||
                    (isZh.value
                        ? '上传失败，请稍后重试'
                        : 'Upload failed, please try again'),
                'error'
            )
            return false
        }
    }

    const openPicker = () => {
        if (!isLoggedIn.value) {
            onRequireLogin?.()
            return
        }
        if (hasBusyItems.value) return
        fileInputRef.value?.click()
    }

    const handleInputChange = (event: Event) => {
        const input = event.target as HTMLInputElement
        const files = input.files ? Array.from(input.files) : []
        input.value = ''
        if (!files.length) return
        void handleReferenceFiles(files)
    }

    const handleDrop = (event: DragEvent) => {
        const files = event.dataTransfer?.files
            ? Array.from(event.dataTransfer.files)
            : []
        if (!files.length) return
        void handleReferenceFiles(files)
    }

    const handleReferenceFiles = async (files: File[]) => {
        if (!files.length) return
        if (!isLoggedIn.value) {
            onRequireLogin?.()
            return
        }
        if (hasBusyItems.value) {
            showNotice(
                isZh.value
                    ? '参考素材仍在处理中，请稍候再试'
                    : 'References are still processing, please wait'
            )
            return
        }

        isHandlingReferences.value = true
        try {
            const batchHasVisualReference = files.some(file => {
                const kind = getAlmightyFileKind(file, isDance25.value)
                return kind === 'image' || kind === 'video'
            })
            const batchHasAudioReference = files.some(file => {
                return getAlmightyFileKind(file, isDance25.value) === 'audio'
            })

            if (
                !isDance25.value &&
                batchHasAudioReference &&
                !hasVisualReference.value &&
                !batchHasVisualReference
            ) {
                showValidationIssue('audio-needs-visual-reference')
                return
            }

            let nextImageCount = images.value.length
            let nextVideoCount = videos.value.length
            let nextAudioCount = audios.value.length
            let nextVideoDuration = videoDurationTotal.value
            let nextAudioDuration = audioDurationTotal.value

            for (const file of files) {
                const kind = getAlmightyFileKind(file, isDance25.value)
                if (kind === 'other') {
                    showNotice(
                        isZh.value
                            ? '仅支持上传图片、视频和音频文件'
                            : 'Only image, video and audio files are supported'
                    )
                    continue
                }

                if (isDance25.value) {
                    const issue = validateDance25ReferenceFile(file, kind)
                    if (issue === 'unsupported-type') {
                        showNotice(
                            isZh.value
                                ? '仅支持上传图片、视频和音频文件'
                                : 'Only image, video and audio files are supported'
                        )
                        continue
                    }
                    if (issue === 'file-too-large') {
                        showNotice(
                            isZh.value
                                ? `参考素材大小不能超过 ${getDance25FileSizeLabel(kind)}`
                                : `Reference files must be within ${getDance25FileSizeLabel(kind)}`
                        )
                        continue
                    }
                }

                if (kind === 'image') {
                    if (!(await validateImageFile(file))) continue
                    if (
                        !showValidationIssue(
                            validateAlmightyImageCount(
                                nextImageCount + 1,
                                imageMaxCount.value
                            )
                        )
                    ) {
                        continue
                    }
                    const uploaded = await uploadReference({ file, kind })
                    if (uploaded) nextImageCount += 1
                    continue
                }

                if (kind === 'video') {
                    if (
                        !showValidationIssue(
                            validateAlmightyVideoLimits({
                                nextCount: nextVideoCount + 1,
                                maxCount: videoMaxCount.value,
                                ...durationLimits.value,
                            })
                        )
                    ) {
                        continue
                    }
                    try {
                        const meta = await readAlmightyVideoMetadata(file)
                        if (
                            isDance25.value &&
                            !isDance25VideoResolutionValid(meta)
                        ) {
                            showNotice(
                                isZh.value
                                    ? '视频参考短边需不小于480，长边不能超过4096'
                                    : 'Video references require a minimum side of 480 and a maximum side of 4096'
                            )
                            continue
                        }
                        const duration = meta.duration
                        const nextTotalDuration = nextVideoDuration + duration
                        if (
                            !showValidationIssue(
                                validateAlmightyVideoLimits({
                                    nextCount: nextVideoCount + 1,
                                    duration,
                                    nextTotalDuration,
                                    maxCount: videoMaxCount.value,
                                    ...durationLimits.value,
                                })
                            )
                        ) {
                            continue
                        }
                        const uploaded = await uploadReference({
                            file,
                            kind,
                            duration,
                        })
                        if (uploaded) {
                            nextVideoCount += 1
                            nextVideoDuration = nextTotalDuration
                        }
                    } catch (error) {
                        showNotice(
                            getAlmightyMediaReadErrorMessage(
                                error,
                                locale.value
                            ),
                            'error'
                        )
                    }
                    continue
                }

                if (
                    !showValidationIssue(
                        validateAlmightyAudioLimits({
                            nextCount: nextAudioCount + 1,
                            maxCount: audioMaxCount.value,
                            ...durationLimits.value,
                        })
                    )
                ) {
                    continue
                }

                try {
                    const duration = await readAlmightyAudioDuration(file)
                    const nextTotalDuration = nextAudioDuration + duration
                    if (
                        !showValidationIssue(
                            validateAlmightyAudioLimits({
                                nextCount: nextAudioCount + 1,
                                duration,
                                nextTotalDuration,
                                maxCount: audioMaxCount.value,
                                ...durationLimits.value,
                            })
                        )
                    ) {
                        continue
                    }
                    const uploaded = await uploadReference({
                        file,
                        kind,
                        duration,
                    })
                    if (uploaded) {
                        nextAudioCount += 1
                        nextAudioDuration = nextTotalDuration
                    }
                } catch (error) {
                    showNotice(
                        getAlmightyMediaReadErrorMessage(error, locale.value),
                        'error'
                    )
                }
            }
        } finally {
            isHandlingReferences.value = false
        }
    }

    const removeReferenceAt = (index: number) => {
        if (index < 0 || index >= items.value.length) return
        const nextItems = items.value.filter((_, itemIndex) => {
            return itemIndex !== index
        })
        const nextHasVisualReference = nextItems.some(item => {
            return item.kind === 'image' || item.kind === 'video'
        })
        const nextHasAudio = nextItems.some(item => item.kind === 'audio')
        if (!isDance25.value && nextHasAudio && !nextHasVisualReference) {
            showValidationIssue('audio-needs-visual-reference')
            return
        }
        items.value = nextItems
    }

    const getItemKindLabel = (kind: AlmightyReferenceKind) => {
        if (kind === 'video') return isZh.value ? '视频' : 'Video'
        if (kind === 'audio') return isZh.value ? '音频' : 'Audio'
        return isZh.value ? '图片' : 'Image'
    }

    return {
        fileInputRef,
        hasVisualReference,
        hasBusyItems,
        hasUploadingItems,
        isHandlingReferences,
        images,
        videos,
        audios,
        videoDurationTotal,
        audioDurationTotal,
        openPicker,
        handleInputChange,
        handleDrop,
        removeReferenceAt,
        getItemKindLabel,
        formatDuration: formatAlmightyDurationSeconds,
        imageMaxCount,
    }
}
